<template>
  <InboxNotificationsWebSocket />
  <div
    :class="{
      'app-layout--legacy': isLegacyLayout,
      'app-layout--pinned-menu': isMenuPinned,
      'app-layout--mobile': isMobile,
    }"
    :style="{
      flexDirection: isMobile ? 'column' : 'row',
    }"
    class="app-layout tw:flex"
  >
    <div :class="classList" class="top-tooltips">
      <div class="container-fluid">
        <div class="right" />
      </div>
    </div>

    <MainMenu
      v-if="!isEnabledWebView && !isMobile"
      :is-pinned="isMenuPinned"
      style="z-index: 200"
      @change="onMenuPinChange()"
      @open-ai-banner="openAiBannerDrawer"
    />

    <div :class="classList" class="app-view">
      <MainTopBar v-if="!isEnabledWebView" />
      <IntegrationStatus v-model="showIntegrationBanner" />
      <PaymentNotification v-if="paymentNotificationData !== null" />
      <MessagesBlockedModal
        v-if="shouldShowMessagesBlockedModal && messagesBlockedModalData"
        :data="messagesBlockedModalData"
        @close="handleCloseMessagesBlockedModal"
      />
      <FreemiumPlanReachedNotification v-if="loaded && usedMessageCredits === 0" />
      <div
        ref="appContainer"
        :class="{
          'app-container__no-padding': noPadding,
          'tw:!h-[calc(100%_-_6.5rem)]': loaded && usedMessageCredits === 0,
        }"
        :style="{ backgroundColor: backgroundColor }"
        class="app-container container-fluid"
      >
        <MainLayoutBlockImport />
        <Transition
          leave-active-class="tw:transition-opacity tw:duration-300 tw:ease-in-out"
          leave-from-class="tw:opacity-100"
          leave-to-class="tw:opacity-0"
          name="fade"
        >
          <div
            v-if="isLoading && reportsOverlay"
            :class="{
              'tw:sm:left-60': isMenuPinned,
              'tw:sm:left-16': !isMenuPinned,
              'tw:sm:w-[calc(100%-15rem)]': isMenuPinned,
              'tw:sm:w-[calc(100%-4rem)]': !isMenuPinned,
              'tw:top-0 tw:h-full': isMobile,
              'tw:sm:top-14 tw:sm:h-[calc(100%-3.5rem)]': !isMobile,
            }"
            class="tw:absolute tw:w-full tw:sm:fixed tw:z-[150] tw:bg-white tw:flex tw:flex-col tw:justify-center tw:items-center tw:text-2xl"
          >
            <EdroneLoading unique-id="main-layout__loading-overlay" />
            <p v-if="loadingText" class="tw:body-2 tw:!text-default-700 tw:mt-4 tw:mb-0 tw:text-center tw:px-4">
              {{ loadingText }}
            </p>
          </div>
        </Transition>

        <main class="tw:h-full">
          <RouterView />
        </main>
      </div>

      <ShoperReinstallNotification />
      <MainOnboardingVideoWidget v-if="showVideo" :video-url="videoUrl ?? undefined" @close="closeVideo" />
      <SmsPackageManageDrawer />
      <WhatsappPackageManagerWrapper />
      <TaskCompleteModal />
      <EdroneConfirmDialog />
      <FreemiumUpgradeDrawer />
    </div>
    <AiBannerDrawer :is-open="isAiBannerDrawerOpen" @close="closeAiBannerDrawer" />

    <MainMenuMobile v-if="isMobile" />
  </div>
</template>

<script lang="ts" setup>
import MainMenu from "../MainMenu/MainMenu.vue";
import MainMenuMobile from "../MainMenu/MainMenuMobile.vue";
import EdroneLoading from "edrone-components/EdroneLoading.vue";
import InboxNotificationsWebSocket from "../InboxNotificationsWebSocket.vue";
import MainTopBar from "vue-app/components/MainLayout/MainTopBar.vue";
import ShoperReinstallNotification from "vue-app/components/MainLayout/ShoperReinstallNotification.vue";
import MainOnboardingVideoWidget from "vue-app/components/MainLayout/MainOnboardingVideoWidget.vue";
import PaymentNotification from "vue-app/components/PaymentNotification/PaymentNotification.vue";
import MessagesBlockedModal from "vue-app/components/MessagesBlockedModal/MessagesBlockedModal.vue";
import TaskCompleteModal from "vue-app/components/TaskCompleteModal/TaskCompleteModal.vue";
import IntegrationStatus from "vue-app/components/MainLayout/IntegrationStatus.vue";
import WhatsappPackageManagerWrapper from "vue-app/components/WhatsappPackageManagerWrapper/WhatsappPackageManagerWrapper.vue";
import FreemiumPlanReachedNotification from "vue-app/components/Freemium/FreemiumPlanReachedNotification.vue";
import FreemiumUpgradeDrawer from "vue-app/components/Freemium/UpgradeDrawer/FreemiumUpgradeDrawer.vue";
import AiBannerDrawer from "vue-app/components/AiBanner/AiBannerDrawer.vue";
import useOnboardingVideo from "vue-app/composables/useOnboardingVideo";
import useAppContainerScroll from "vue-app/composables/useAppContainerScroll";
import MainLayoutBlockImport from "vue-app/components/MainLayout/MainLayoutBlockImport.vue";
import { getDashboardNotifications } from "vue-app/api/NotificationService";
import { ref, provide, computed, onMounted, onBeforeMount, onBeforeUnmount, watch } from "vue";
import { INTEGRATION_BANNER_HEIGHT, PAYMENT_BAR_HEIGHT, TOP_BAR_HEIGHT } from "vue-app/types/enums";
import SmsPackageManageDrawer from "vue-app/components/SMS/SMSPackageManage/SMSPackageManageDrawer.vue";
import EdroneConfirmDialog from "edrone-components/EdroneConfirmDialog/EdroneConfirmDialog.vue";
import { useWebSocket } from "vue-app/composables/useWebSocket";
import { openTaskCompleteModal } from "vue-app/components/TaskCompleteModal/useTaskCompleteModal";
import { useMachine } from "@xstate-5/vue";
import { initializeFreemiumMachine, symbolMachineFreemium } from "vue-app/components/Freemium/Freemium.machine";
import { inspector } from "vue-app/utils/xstate";
import { ReactNativeBridge } from "vue-app/utils/reactNativeBridge";
import { updateIntercom } from "vue-app/utils/functions";
import { useFreemiumStore } from "vue-app/components/Freemium/Freemium.store";
import { storeToRefs } from "pinia";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import { useGlobalLoader } from "vue-app/composables/useGlobalLoader";
import { DTOActionRequiredNotification } from "vue-app/api/dto";
import { getCookie } from "vue-app/utils/cookies";

const store = useStore();
const route = useRoute();
const router = useRouter();

const { showVideo, videoUrl, closeVideo } = useOnboardingVideo();
const { appContainer } = useAppContainerScroll();

const paymentNotificationData = ref<null | DTOActionRequiredNotification>(null);
const messagesBlockedModalData = ref<null | DTOActionRequiredNotification>(null);
const showIntegrationBanner = ref(false);
const backgroundColor = ref("#ffffff");
const noPadding = ref(false);
const isLegacyLayout = ref(false);
const isMenuPinned = ref(true);
const localIsDev = ref(false);
const trackerId = window.EDRONE_CONTEXT.trackerId;
const agentId = window.EDRONE_CONTEXT.user.agentId;
const reportsOverlay = ref(true);
const isEnabledWebView = ReactNativeBridge.checkIsWebViewMode();
const intercomIconPositionChanged = ref(false);
const intercomChangeClasses = ["CommonFooter", "EdroneNavigationBottom", "CustomerPreview"];
const observer = ref<MutationObserver | null>(null);
const isAiBannerDrawerOpen = ref(false);
const hasNewAiBannerContent = ref(false);

const shouldShowMessagesBlockedModal = computed(() => {
  if (!messagesBlockedModalData.value) return false;
  if (!window.EDRONE_CONTEXT.tracker.isPaying) return false;
  const type = messagesBlockedModalData.value.notificationType === "NO_SMS_CREDITS" ? "sms" : "whatsapp";
  const hideUntil = getCookie(type + "BlockedModalHideUntil_" + window.EDRONE_CONTEXT.trackerId);
  if (hideUntil) {
    return false;
  }
  return Number(messagesBlockedModalData.value.details["blocked_messages"] || 0) > 0;
});

const handleCloseMessagesBlockedModal = () => {
  messagesBlockedModalData.value = null;
};

const getNotifications = async (): Promise<void> => {
  try {
    const data = await getDashboardNotifications();
    paymentNotificationData.value = data.find((n) => n.notificationType === "PAYMENT_OVERDUE") || null;
    messagesBlockedModalData.value = data.find((n) => n.notificationType === "NO_SMS_CREDITS") || null;
    if (messagesBlockedModalData.value === null) {
      messagesBlockedModalData.value = data.find((n) => n.notificationType === "NO_WHATSAPP_CREDITS") || null;
    }
  } catch (e) {
    console.error(e);
  }
};
getNotifications();

const appContainerOffset = computed(() => {
  let offset = TOP_BAR_HEIGHT;
  if (paymentNotificationData.value !== null) {
    offset += PAYMENT_BAR_HEIGHT;
  }
  if (showIntegrationBanner.value) {
    offset += INTEGRATION_BANNER_HEIGHT;
  }
  return `${offset}rem`;
});

const freemiumStore = useFreemiumStore();
const { limitMessageCredits, usedMessageCredits, loaded } = storeToRefs(freemiumStore);

const url = `wss://qev7les36f.execute-api.eu-west-1.amazonaws.com/PROD?agent_id=${agentId}&tracker_id=${trackerId}`;

useWebSocket(url, (event) => {
  try {
    const data = JSON.parse(event.data);
    if (data.type === "TASK_COMPLETED") {
      const options = JSON.parse(data.message);
      if (options.prize) {
        limitMessageCredits.value = options.newLimit;
        usedMessageCredits.value += options.prize;
        openTaskCompleteModal(options);
      }
    }
  } catch (error) {
    console.error("Error parsing WebSocket message:", error);
  }
});

const { isLoading, loadingText } = useGlobalLoader();
const roles = computed(() => store.getters["global/roles"]);
const isDev = computed(() => store.getters["global/isDev"]);
const isMobile = computed(() => store.getters["global/isMobile"]);

const classList = computed(() => ({
  "app-view--menu-pinned": isMenuPinned.value,
}));

const onMenuPinChange = (): void => {
  isMenuPinned.value = !isMenuPinned.value;
  localStorage.setItem(`${trackerId}-${agentId}-menu-pin`, isMenuPinned.value.toString());
};

const updateLayout = (): void => {
  isLegacyLayout.value = route.meta.legacyLayout === true;
  noPadding.value = route.meta.noPadding === true;
  backgroundColor.value = (route.meta.backgroundColor as string) || "#ffffff";
};

const verifyRouteRoles = (): boolean => {
  // eslint-disable-next-line no-prototype-builtins
  const hasRequiredRole = route.meta && route.meta.hasOwnProperty("requiredRoles");
  return !hasRequiredRole || (hasRequiredRole && (route.meta.requiredRoles as string[]).every((r) => roles.value[r]));
};

const verifyAccessToRoute = (): void => {
  const rolesPermitted = verifyRouteRoles();
  if (!rolesPermitted) {
    router.push({
      path: `/app/access-denied?p=${route.meta.routeName}`,
    });
  }
};

const checkWindowWidth = (): void => {
  store.dispatch("global/setIsMobile", document.documentElement.clientWidth < 992);
};

const checkForIntercomPosition = (): void => {
  const found = intercomChangeClasses.some((cls) => document.querySelector(`.${cls}`));
  if (found !== intercomIconPositionChanged.value) {
    intercomIconPositionChanged.value = found;
    if (intercomIconPositionChanged.value) {
      updateIntercom("vertical_padding", "90");
    } else {
      updateIntercom("vertical_padding", "20");
    }
  }
};

watch(route, () => {
  updateLayout();
});

onBeforeMount(() => {
  store.dispatch("global/setInitialState", window.EDRONE_CONTEXT);
  if (localStorage && localStorage.getItem(`${trackerId}-${agentId}-menu-pin`)) {
    isMenuPinned.value = localStorage.getItem(`${trackerId}-${agentId}-menu-pin`) === "true";
  } else {
    isMenuPinned.value = true;
  }
  updateLayout();
  verifyAccessToRoute();

  const freemiumActor = useMachine(initializeFreemiumMachine(), { inspect: inspector().inspect });
  provide(symbolMachineFreemium, freemiumActor);
});

onMounted(() => {
  localIsDev.value = isDev.value;
  window.addEventListener("resize", checkWindowWidth);
  checkWindowWidth();

  const journey = document.createElement("script");
  journey.setAttribute("src", "https://embed.journey.io/journey-lightbox.umd.js");
  journey.setAttribute("async", "true");
  document.head.appendChild(journey);

  const bc = new BroadcastChannel("Workspace");
  bc.onmessage = (event) => {
    if (event.data === "RELOAD_TO_DASHBOARD") {
      window.location.href = "/app/live-events";
    }
  };

  observer.value = new MutationObserver(() => {
    checkForIntercomPosition();
  });

  observer.value.observe(document.body, {
    childList: true,
    subtree: true,
  });

  checkForIntercomPosition();
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", checkWindowWidth);
  if (observer.value) {
    observer.value.disconnect();
  }
});

const offset = computed(() => (isEnabledWebView ? "100%" : `calc(100% - ${appContainerOffset.value})`));

const openAiBannerDrawer = () => {
  isAiBannerDrawerOpen.value = true;
  hasNewAiBannerContent.value = false;
};

const closeAiBannerDrawer = () => {
  isAiBannerDrawerOpen.value = false;
};
</script>

<style>
.grecaptcha-badge {
  visibility: hidden !important;
}

body {
  overflow: hidden;
  container-type: inline-size;
}

@media (max-width: 62rem) {
  body {
    /* MobileApp safe area insets */
    padding-top: env(safe-area-inset-top);
    padding-bottom: env(safe-area-inset-bottom);
    padding-left: env(safe-area-inset-left);
    padding-right: env(safe-area-inset-right);
  }

  .app-layout {
    overflow: hidden;
  }

  .intercom-lightweight-app > div,
  .intercom-app > div {
    bottom: 5.625rem !important;
  }

  .tw:z-\[41000\] ~ * [class*="intercom-"][class*="-namespace-"],
  .tw:z-\[41000\] ~ [class*="intercom-"][class*="-namespace-"] {
    display: none !important;
  }
}
</style>
<style scoped>
.app-layout {
  position: relative;
  background: #fafafa;
  transition: background-color 0.3s ease-in-out;
  height: 100%;
}

.app-layout.app-layout--legacy .app-container {
  padding-left: 0;
  padding-right: 0;
}

.app-view {
  width: 100%;
  height: 100%;
  margin: 0;
  position: relative;
  container-type: inline-size;
}

.app-container {
  height: v-bind("offset");
  overflow-y: auto;
  overflow-x: hidden;
  --bs-gutter-x: 2rem !important;
  padding-right: var(--bs-gutter-x) !important;
  padding-left: var(--bs-gutter-x) !important;
}

.app-container__no-padding {
  padding-right: 0 !important;
  padding-left: 0 !important;
}

.app-layout--mobile .app-view {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
}

.app-layout--mobile .app-container {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
}
</style>
